/**
 * Regjistrimi i një servisimi.
 *
 * `Servisi i radhës` nuk është zbukurim: një mirëmbajtje e planifikuar që s'ka
 * datë nuk kujtohet nga askush, dhe pikërisht ajo është arsyeja pse pajisjet
 * shkojnë në servis vonë.
 *
 * Statusi "Në proces" prek statusin e pajisjes — por vetëm nëse nuk e mban
 * dikush. Kjo thuhet këtu, para klikimit, sepse ndryshe përdoruesi e sheh
 * ndryshimin te tabela dhe nuk e kupton nga erdhi.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Grid, Alert, Box, Typography, InputAdornment, CircularProgress,
} from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import { SERVICE_STATUSES, assetTitle } from '../../config/assets';

export default function ServiceDialog({ open, asset, onClose, onSave, today }) {
  const [form, setForm] = useState({
    serviceDate: '', provider: '', description: '', cost: '',
    invoiceNo: '', status: 'completed', nextServiceDate: '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy]   = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      serviceDate: today || new Date().toISOString().slice(0, 10),
      provider: '', description: '', cost: '', invoiceNo: '',
      status: 'completed', nextServiceDate: '',
    });
  }, [open, today]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const invalid = !form.provider.trim() && !form.description.trim();

  async function submit() {
    if (invalid) { setError('Shkruaj së paku ofruesin e servisit ose përshkrimin.'); return; }
    setBusy(true); setError('');
    try {
      await onSave({ ...form, cost: form.cost === '' ? null : Number(form.cost) });
    } catch (e) {
      setError(e.message || 'Regjistrimi dështoi.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: 'primary.soft', color: 'primary.main',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <BuildOutlinedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>Regjistro servis</Typography>
          <Typography variant="caption" color="text.secondary">
            {asset ? `${asset.assetCode} · ${assetTitle(asset)}` : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {!!error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField type="date" label="Data e servisimit" fullWidth size="small"
              InputLabelProps={{ shrink: true }} value={form.serviceDate} onChange={set('serviceDate')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Statusi" fullWidth size="small" value={form.status} onChange={set('status')}>
              {SERVICE_STATUSES.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Ofruesi i servisit" fullWidth size="small"
              value={form.provider} onChange={set('provider')} placeholder="iSTYLE, servisi i brendshëm…" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Fatura / Nr. dokumentit" fullWidth size="small"
              value={form.invoiceNo} onChange={set('invoiceNo')} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Përshkrimi i punës" fullWidth size="small" multiline minRows={2}
              value={form.description} onChange={set('description')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField type="number" label="Kosto" fullWidth size="small"
              value={form.cost} onChange={set('cost')}
              InputProps={{ endAdornment: <InputAdornment position="end">{asset?.currency || 'EUR'}</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField type="date" label="Servisi i radhës" fullWidth size="small"
              InputLabelProps={{ shrink: true }} value={form.nextServiceDate} onChange={set('nextServiceDate')}
              helperText="Pa datë, mirëmbajtja nuk kujtohet." />
          </Grid>
        </Grid>

        {form.status === 'in_progress' && !asset?.holder && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Pajisja do të kalojë në statusin <strong>Në servis</strong> dhe nuk do të mund të dorëzohet derisa
            ta ndryshosh statusin.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" onClick={submit} disabled={busy || invalid}
          startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Ruaj servisimin
        </Button>
      </DialogActions>
    </Dialog>
  );
}
